import type { AutorespondersInput } from '@pypestream/api-services/urql';

import type { Pype } from '../xstate/prostudio.api-service.types';
import {
  CONTACT_CENTER_INTEGRATION_NAME,
  isContactCenterPlatform,
} from './platform-catalog';
import { toIntegrationConfig } from './pype-agent-routing';

/**
 * Default Messages (PSTUDIO-3803) as data, kept out of the page so it can be
 * unit-tested without rendering it.
 *
 * Two kinds of message live on a pype's `autoresponders`:
 *
 * - `busy` / `offline` belong to the channel's Live Agent Platform and are edited
 *   per channel;
 * - `technical_error` ("Platform error") fires on automation errors regardless of
 *   the escalation platform, so the page shows ONE field for the whole env. The
 *   backend still stores it per pype, so saving that field writes it to every
 *   channel — except Contact Center ones, whose copy is managed in Contact
 *   Center (product decision on the ticket, 2026-10-14).
 */

export interface MessageState {
  enable: boolean;
  text: string;
}

export interface PlatformMessages {
  busy: MessageState;
  offline: MessageState;
  technicalError: MessageState;
}

export const EMPTY_MESSAGE: MessageState = { enable: false, text: '' };

export const toPlatformMessages = (pype: Pype): PlatformMessages => ({
  offline: {
    enable: pype.autoresponders?.offline?.enable ?? false,
    text: pype.autoresponders?.offline?.text ?? '',
  },
  busy: {
    enable: pype.autoresponders?.busy?.enable ?? false,
    text: pype.autoresponders?.busy?.text ?? '',
  },
  technicalError: {
    enable: pype.autoresponders?.technical_error?.enable ?? false,
    text: pype.autoresponders?.technical_error?.text ?? '',
  },
});

export const toAutorespondersInput = (
  messages: PlatformMessages
): AutorespondersInput => ({
  busy: messages.busy,
  offline: messages.offline,
  technicalError: messages.technicalError,
});

/**
 * Whether a channel's copy is managed in Contact Center.
 *
 * The routing value alone is not enough: on candidate (2026-10-14) every Contact
 * Center channel routes as `external_agent`, with the integration named in
 * `agent_routing_config` — the shape `create-channel.ts` writes — and none uses
 * the legacy `cc` value `isContactCenterPlatform` checks. Reading only the
 * routing left those channels editable here, and would have let the single
 * Platform error field overwrite their copy.
 */
export const isContactCenterPype = (pype: Pype): boolean =>
  isContactCenterPlatform(pype.agent_routing) ||
  (
    toIntegrationConfig(pype.agent_routing_config).integration_name ?? ''
  ).toLowerCase() === CONTACT_CENTER_INTEGRATION_NAME.toLowerCase();

/**
 * The channels the single Platform error field reads from and writes to: not
 * Contact Center, and with autoresponders actually loaded. A channel whose
 * autoresponders could not be merged in (`null`, see get-default-messages-pypes)
 * is left out — saving the field sends that channel's FULL autoresponders, so
 * writing one we never read would replace its real busy/offline with empties.
 */
export const takesPlatformError = (pype: Pype): boolean =>
  Boolean(pype.id) && pype.autoresponders != null && !isContactCenterPype(pype);

const sameMessage = (a: MessageState, b: MessageState): boolean =>
  a.enable === b.enable && a.text === b.text;

/**
 * What the Platform error field shows. The channels can disagree today (it was
 * edited per channel until now): the field then shows the first channel's value
 * and `mixed` tells the page to warn that saving applies it to all of them.
 */
export const resolvePlatformError = (
  pypes: Pype[]
): { value: MessageState; mixed: boolean } => {
  const values = pypes
    .filter(takesPlatformError)
    .map((pype) => toPlatformMessages(pype).technicalError);
  const [first] = values;

  if (!first) {
    return { value: EMPTY_MESSAGE, mixed: false };
  }

  return {
    value: first,
    mixed: values.some((value) => !sameMessage(value, first)),
  };
};

/**
 * One save per pype, each carrying that pype's FULL autoresponders — the save
 * chain and the bulk invoke stay exactly as they were, and nothing relies on how
 * the backend would merge a partial `autoresponders` object.
 *
 * `platformError` is set only when the field was edited. Then every channel it
 * applies to is saved with it, and with its OWN busy/offline (draft if edited,
 * loaded otherwise) — so a channel without a Live Agent Platform keeps whatever
 * busy/offline it already had, even though the page no longer shows them.
 */
export const buildDefaultMessagesSaves = ({
  pypes,
  drafts,
  touchedIds,
  platformError,
}: {
  pypes: Pype[];
  drafts: Record<string, PlatformMessages>;
  touchedIds: ReadonlySet<string>;
  platformError?: MessageState;
}): { pypeId: string; autoresponders: AutorespondersInput }[] =>
  pypes.flatMap((pype) => {
    if (!pype.id) {
      return [];
    }

    const writesPlatformError =
      platformError !== undefined && takesPlatformError(pype);

    if (!writesPlatformError && !touchedIds.has(pype.id)) {
      return [];
    }

    const messages = drafts[pype.id] ?? toPlatformMessages(pype);

    return [
      {
        pypeId: pype.id,
        autoresponders: toAutorespondersInput(
          writesPlatformError
            ? { ...messages, technicalError: platformError }
            : messages
        ),
      },
    ];
  });

/**
 * The Contact Center link target. Only an absolute http(s) URL becomes a link;
 * anything else (`javascript:`, `data:`, relative, unparseable) is dropped.
 */
export const toExternalUrl = (value?: string | null): string | undefined => {
  try {
    const url = new URL(value ?? '');
    return url.protocol === 'https:' || url.protocol === 'http:'
      ? url.href
      : undefined;
  } catch {
    return undefined;
  }
};
